/**
 * Script Editor Panel
 * Sprint 19: Custom Template Creation
 * 
 * Per-scene script editing with word count, duration estimate, and AI generation
 * Uses existing AIScriptService (backend proxy with GPT)
 */

import React, { useMemo, useState } from 'react';
import { Input, Space, Tag, Tooltip, Button, Modal, Select, message } from 'antd';
import { 
  ClockCircleOutlined, 
  EditOutlined, 
  RobotOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { Sparkles } from 'lucide-react';
import { TemplateScript, TemplateCategory } from '../../../../types/template';
import { AIScriptService, ScriptTone, AIScriptResponse } from '../../services/aiScriptService';
import './ScriptEditorPanel.scss';

const { TextArea } = Input;

interface ScriptEditorPanelProps {
  script: TemplateScript;
  onChange: (content: string) => void;
  defaultExample?: string;
  category?: TemplateCategory;
  sceneTitle?: string;
  suggestedDuration?: number;
}

const WORDS_PER_MINUTE = 150;

export const ScriptEditorPanel: React.FC<ScriptEditorPanelProps> = ({
  script,
  onChange,
  defaultExample,
  category = 'content',
  sceneTitle,
  suggestedDuration = 60
}) => {
  const [isAIModalVisible, setIsAIModalVisible] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiTopic, setAITopic] = useState('');
  const [aiTone, setAITone] = useState<ScriptTone>('professional');
  const [generatedScript, setGeneratedScript] = useState<AIScriptResponse | null>(null);
  const [regenerationFeedback, setRegenerationFeedback] = useState('');

  const wordCount = useMemo(() => {
    if (!script.content) return 0;
    return script.content.trim().split(/\s+/).filter(Boolean).length;
  }, [script.content]);

  const estimatedDuration = useMemo(() => {
    const minutes = wordCount / WORDS_PER_MINUTE;
    const seconds = Math.round(minutes * 60);
    return {
      minutes: Math.floor(seconds / 60),
      seconds: seconds % 60,
      totalSeconds: seconds
    };
  }, [wordCount]);

  const formatDuration = () => {
    const { minutes, seconds } = estimatedDuration;
    if (minutes === 0) return `~${seconds}s`;
    return `~${minutes}m ${seconds}s`;
  };

  const handleGenerateScript = async () => {
    if (!aiTopic.trim()) {
      message.warning('Please enter a topic for your script');
      return;
    }

    setIsGenerating(true);
    try {
      const result = await AIScriptService.generateScript({
        templateType: 'custom',
        category,
        userTopic: aiTopic,
        tone: aiTone,
        duration: suggestedDuration,
        sceneTitle: sceneTitle || script.sceneTitle,
        sceneContext: script.content || undefined
      });

      setGeneratedScript(result);
      message.success(`Generated ${result.wordCount} words (~${Math.round(result.estimatedDuration)}s)`);
    } catch (error: any) {
      message.error(error.message || 'Failed to generate script');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateScript = async () => {
    if (!regenerationFeedback.trim()) {
      message.warning('Please provide feedback for regeneration');
      return;
    }

    setIsGenerating(true);
    try {
      const result = await AIScriptService.regenerateScript(
        {
          templateType: 'custom',
          category,
          userTopic: aiTopic,
          tone: aiTone,
          duration: suggestedDuration,
          sceneTitle: sceneTitle || script.sceneTitle
        },
        regenerationFeedback
      );

      setGeneratedScript(result);
      setRegenerationFeedback('');
      message.success('Script regenerated with your feedback!');
    } catch (error: any) {
      message.error(error.message || 'Failed to regenerate script');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseGeneratedScript = () => {
    if (generatedScript) {
      onChange(generatedScript.script);
      setIsAIModalVisible(false);
      setGeneratedScript(null);
      setAITopic('');
      message.success('AI script applied!');
    }
  };

  return (
    <div className="script-editor-panel">
      <div className="script-header">
        <h4>
          <EditOutlined /> Script for: {script.sceneTitle}
        </h4>
        <Space className="script-stats">
          <Tag>{wordCount} words</Tag>
          <Tooltip title="Estimated speaking duration at 150 WPM">
            <Tag icon={<ClockCircleOutlined />}>{formatDuration()}</Tag>
          </Tooltip>
        </Space>
      </div>

      <div className="ai-generate-section">
        <Button
          type="primary"
          icon={<RobotOutlined />}
          onClick={() => setIsAIModalVisible(true)}
          className="ai-generate-btn"
        >
          Generate with AI
        </Button>
        <span className="ai-hint">
          Let AI write your script based on a topic and tone
        </span>
      </div>

      <TextArea
        value={script.content}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your script content here, or use AI to generate it..."
        rows={8}
        className="script-textarea"
      />

      {script.customizationNote && (
        <div className="customization-note">
          <strong>Tip:</strong> {script.customizationNote}
        </div>
      )}

      {defaultExample && script.content !== defaultExample && (
        <div className="example-section">
          <div className="example-header">
            <span>Example:</span>
            <Button type="link" size="small" onClick={() => onChange(defaultExample)}>
              Use this example
            </Button>
          </div>
          <div className="example-content">{defaultExample}</div>
        </div>
      )}

      <Modal
        title="AI Script Generator"
        open={isAIModalVisible}
        onCancel={() => setIsAIModalVisible(false)}
        width={700}
        footer={null}
      >
        <div className="ai-generator-content">
          <div className="ai-input-section">
            <div className="input-group">
              <label>What is this scene about?</label>
              <Input
                value={aiTopic}
                onChange={(e) => setAITopic(e.target.value)}
                placeholder="e.g., Introduce our new product features..."
                size="large"
              />
            </div>

            <div className="input-row">
              <div className="input-group">
                <label>Tone</label>
                <Select
                  value={aiTone}
                  onChange={setAITone}
                  style={{ width: '100%' }}
                >
                  <Select.Option value="professional">Professional</Select.Option>
                  <Select.Option value="casual">Casual</Select.Option>
                  <Select.Option value="energetic">Energetic</Select.Option>
                </Select>
              </div>
              <div className="input-group">
                <label>Target Duration</label>
                <span className="duration-value">
                  {suggestedDuration}s (~{Math.round(suggestedDuration * 150 / 60)} words)
                </span>
              </div>
            </div>

            {!generatedScript && (
              <Button
                type="primary"
                icon={<Sparkles size={16} />}
                onClick={handleGenerateScript}
                loading={isGenerating}
                disabled={!aiTopic.trim()}
                size="large"
                block
              >
                {isGenerating ? 'Generating...' : 'Generate Script'}
              </Button>
            )}
          </div>

          {generatedScript && generatedScript.script && generatedScript.wordCount > 0 && (
            <div className="generated-script-section">
              <div className="generated-header">
                <h4>Generated Script</h4>
                <Space>
                  <Tag>{generatedScript.wordCount} words</Tag>
                  <Tag icon={<ClockCircleOutlined />}>
                    ~{Math.round(generatedScript.estimatedDuration)}s
                  </Tag>
                </Space>
              </div>

              <TextArea
                value={generatedScript.script}
                rows={6}
                readOnly
                className="generated-preview"
              />

              <div className="regenerate-section">
                <Input
                  value={regenerationFeedback}
                  onChange={(e) => setRegenerationFeedback(e.target.value)}
                  placeholder="Make it shorter, add a joke, be more formal..."
                />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRegenerateScript}
                  loading={isGenerating}
                  disabled={!regenerationFeedback.trim()}
                >
                  Regenerate
                </Button>
              </div>

              <Button
                type="primary"
                onClick={handleUseGeneratedScript}
                block
                size="large"
                style={{ marginTop: 16 }}
              >
                Use This Script
              </Button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ScriptEditorPanel;
